/**
 * Message System Constants
 * Comprehensive enumeration of all message types and configurations
 * used throughout the messaging system
 */

// Import base constants
import {
  MESSAGE_PRIORITY,
  MESSAGE_SECURITY_LEVELS,
  MESSAGE_STATUS,
  MESSAGE_ROLES,
  MESSAGE_FLOW,
  MESSAGE_BUSINESS_HOURS
} from './messages/baseMessageConstants';

// Import variation-specific messages
import {
  VARIATION_MESSAGE_TYPES,
  RETAKE_REASONS as VARIATION_RETAKE_REASONS,
  RETAKE_REASON_CONFIG as VARIATION_RETAKE_REASON_CONFIG,
  VARIATION_MESSAGE_TYPE_CONFIG,
  VARIATION_MESSAGE_RATE_LIMITS,
  VARIATION_MESSAGE_TYPE_HOURS,
  VARIATION_MESSAGE_TEMPLATE_KEYS,
  getRetakeReasonConfig,
  getRetakeReasonOptions
} from './messages/variationMessages';

// Re-export base constants for backward compatibility
export {
  MESSAGE_PRIORITY,
  MESSAGE_SECURITY_LEVELS,
  MESSAGE_STATUS,
  MESSAGE_ROLES,
  MESSAGE_FLOW,
  MESSAGE_BUSINESS_HOURS
};

// =========================================================
// MESSAGE TYPE DEFINITIONS
// =========================================================

/**
 * Complete enumeration of all message types in the system
 */
export const MESSAGE_TYPES = {
  // General Communication
  GENERAL: 'GENERAL',

  // Opportunity & Application Flow
  OPPORTUNITY_INQUIRY: 'OPPORTUNITY_INQUIRY',
  VOICE_ACTOR_APPLICATION: 'VOICE_ACTOR_APPLICATION',
  APPLICATION_RESPONSE: 'APPLICATION_RESPONSE',
  APPLICATION_STATUS_UPDATE: 'APPLICATION_STATUS_UPDATE',

  // Negotiation & Contract
  NEGOTIATION: 'NEGOTIATION',
  RATE_NEGOTIATION: 'RATE_NEGOTIATION',
  CONTRACT_DISCUSSION: 'CONTRACT_DISCUSSION',

  // Project Management
  PROJECT_UPDATE: 'PROJECT_UPDATE',
  PROJECT_MILESTONE: 'PROJECT_MILESTONE',

  // Financial
  FINANCIAL_TRANSACTION: 'FINANCIAL_TRANSACTION',

  // Delivery & Completion
  DELIVERY_CONFIRMATION: 'DELIVERY_CONFIRMATION',

  // Security-defined types (from SecurityRegistry)
  MESSAGE_VOICE_ACTOR_APPLICATION: 'MESSAGE_VOICE_ACTOR_APPLICATION',
  MESSAGE_FINANCIAL_TRANSACTION: 'MESSAGE_FINANCIAL_TRANSACTION',

  // Variation Recording Workflow Messages (imported from variationMessages)
  ...VARIATION_MESSAGE_TYPES
};

// Re-export variation-specific constants for backward compatibility
export const RETAKE_REASONS = VARIATION_RETAKE_REASONS;
export const RETAKE_REASON_CONFIG = VARIATION_RETAKE_REASON_CONFIG;

// =========================================================
// MESSAGE TYPE CONFIGURATION
// =========================================================

/**
 * Message Type Configuration
 * Maps each message type to its properties
 */
export const MESSAGE_TYPE_CONFIG = {
  [MESSAGE_TYPES.GENERAL]: {
    priority: MESSAGE_PRIORITY.LOW,
    security: MESSAGE_SECURITY_LEVELS.PRIVATE,
    flow: MESSAGE_FLOW.BIDIRECTIONAL,
    icon: 'comment',
    color: 'grey',
    displayName: 'General Message',
    requiresAuth: true
  },

  [MESSAGE_TYPES.OPPORTUNITY_INQUIRY]: {
    priority: MESSAGE_PRIORITY.MEDIUM,
    security: MESSAGE_SECURITY_LEVELS.PRIVATE,
    flow: MESSAGE_FLOW.VOICE_ACTOR_TO_BOOK_CREATOR,
    icon: 'briefcase',
    color: 'blue',
    displayName: 'Opportunity Inquiry',
    requiresAuth: true
  },

  [MESSAGE_TYPES.VOICE_ACTOR_APPLICATION]: {
    priority: MESSAGE_PRIORITY.HIGH,
    security: MESSAGE_SECURITY_LEVELS.CONFIDENTIAL,
    flow: MESSAGE_FLOW.VOICE_ACTOR_TO_BOOK_CREATOR,
    icon: 'file alternate',
    color: 'green',
    displayName: 'Voice Actor Application',
    requiresAuth: true,
    requiresProfile: true
  },

  [MESSAGE_TYPES.APPLICATION_RESPONSE]: {
    priority: MESSAGE_PRIORITY.HIGH,
    security: MESSAGE_SECURITY_LEVELS.CONFIDENTIAL,
    flow: MESSAGE_FLOW.BOOK_CREATOR_TO_VOICE_ACTOR,
    icon: 'reply',
    color: 'teal',
    displayName: 'Application Response',
    requiresAuth: true
  },

  [MESSAGE_TYPES.APPLICATION_STATUS_UPDATE]: {
    priority: MESSAGE_PRIORITY.HIGH,
    security: MESSAGE_SECURITY_LEVELS.CONFIDENTIAL,
    flow: MESSAGE_FLOW.BOOK_CREATOR_TO_VOICE_ACTOR,
    icon: 'check circle',
    color: 'olive',
    displayName: 'Application Status Update',
    requiresAuth: true
  },

  [MESSAGE_TYPES.NEGOTIATION]: {
    priority: MESSAGE_PRIORITY.HIGH,
    security: MESSAGE_SECURITY_LEVELS.CONFIDENTIAL,
    flow: MESSAGE_FLOW.BIDIRECTIONAL,
    icon: 'handshake',
    color: 'yellow',
    displayName: 'Negotiation',
    requiresAuth: true
  },

  [MESSAGE_TYPES.RATE_NEGOTIATION]: {
    priority: MESSAGE_PRIORITY.HIGH,
    security: MESSAGE_SECURITY_LEVELS.CONFIDENTIAL,
    flow: MESSAGE_FLOW.BIDIRECTIONAL,
    icon: 'dollar',
    color: 'yellow',
    displayName: 'Rate Negotiation',
    requiresAuth: true
  },

  [MESSAGE_TYPES.CONTRACT_DISCUSSION]: {
    priority: MESSAGE_PRIORITY.HIGH,
    security: MESSAGE_SECURITY_LEVELS.CRITICAL,
    flow: MESSAGE_FLOW.BIDIRECTIONAL,
    icon: 'file contract',
    color: 'purple',
    displayName: 'Contract Discussion',
    requiresAuth: true,
    requiresAudit: true
  },

  [MESSAGE_TYPES.PROJECT_UPDATE]: {
    priority: MESSAGE_PRIORITY.MEDIUM,
    security: MESSAGE_SECURITY_LEVELS.PRIVATE,
    flow: MESSAGE_FLOW.BIDIRECTIONAL,
    icon: 'tasks',
    color: 'blue',
    displayName: 'Project Update',
    requiresAuth: true
  },

  [MESSAGE_TYPES.PROJECT_MILESTONE]: {
    priority: MESSAGE_PRIORITY.HIGH,
    security: MESSAGE_SECURITY_LEVELS.PRIVATE,
    flow: MESSAGE_FLOW.VOICE_ACTOR_TO_BOOK_CREATOR,
    icon: 'flag checkered',
    color: 'green',
    displayName: 'Project Milestone',
    requiresAuth: true
  },

  [MESSAGE_TYPES.FINANCIAL_TRANSACTION]: {
    priority: MESSAGE_PRIORITY.CRITICAL,
    security: MESSAGE_SECURITY_LEVELS.CRITICAL,
    flow: MESSAGE_FLOW.SYSTEM_TO_USER,
    icon: 'money bill wave',
    color: 'green',
    displayName: 'Financial Transaction',
    requiresAuth: true,
    requiresAudit: true,
    requiresEncryption: true
  },

  [MESSAGE_TYPES.DELIVERY_CONFIRMATION]: {
    priority: MESSAGE_PRIORITY.HIGH,
    security: MESSAGE_SECURITY_LEVELS.PRIVATE,
    flow: MESSAGE_FLOW.VOICE_ACTOR_TO_BOOK_CREATOR,
    icon: 'truck',
    color: 'green',
    displayName: 'Delivery Confirmation',
    requiresAuth: true
  },

  // Merge in variation message type configurations
  ...VARIATION_MESSAGE_TYPE_CONFIG
};

// =========================================================
// MESSAGE RATE LIMITING
// =========================================================

/**
 * Message Rate Limiting Configuration
 * Defines hourly and daily limits for each message type
 */
export const MESSAGE_RATE_LIMITS = {
  [MESSAGE_TYPES.GENERAL]: {
    hourly: 50,
    daily: 500
  },
  [MESSAGE_TYPES.OPPORTUNITY_INQUIRY]: {
    hourly: 20,
    daily: 100
  },
  [MESSAGE_TYPES.VOICE_ACTOR_APPLICATION]: {
    hourly: 10,
    daily: 50
  },
  [MESSAGE_TYPES.APPLICATION_RESPONSE]: {
    hourly: 20,
    daily: 100
  },
  [MESSAGE_TYPES.APPLICATION_STATUS_UPDATE]: {
    hourly: 20,
    daily: 100
  },
  [MESSAGE_TYPES.NEGOTIATION]: {
    hourly: 30,
    daily: 200
  },
  [MESSAGE_TYPES.RATE_NEGOTIATION]: {
    hourly: 20,
    daily: 100
  },
  [MESSAGE_TYPES.CONTRACT_DISCUSSION]: {
    hourly: 20,
    daily: 100
  },
  [MESSAGE_TYPES.PROJECT_UPDATE]: {
    hourly: 30,
    daily: 200
  },
  [MESSAGE_TYPES.PROJECT_MILESTONE]: {
    hourly: 10,
    daily: 50
  },
  [MESSAGE_TYPES.FINANCIAL_TRANSACTION]: {
    hourly: 5,
    daily: 20
  },
  [MESSAGE_TYPES.DELIVERY_CONFIRMATION]: {
    hourly: 10,
    daily: 50
  },

  // Merge in variation message rate limits
  ...VARIATION_MESSAGE_RATE_LIMITS
};

// =========================================================
// BUSINESS HOURS CONFIGURATION
// =========================================================

/**
 * Maps message types to business hour restrictions
 */
export const MESSAGE_TYPE_HOURS = {
  [MESSAGE_TYPES.GENERAL]: MESSAGE_BUSINESS_HOURS.ALWAYS_AVAILABLE,
  [MESSAGE_TYPES.OPPORTUNITY_INQUIRY]: MESSAGE_BUSINESS_HOURS.STANDARD,
  [MESSAGE_TYPES.VOICE_ACTOR_APPLICATION]: MESSAGE_BUSINESS_HOURS.STANDARD,
  [MESSAGE_TYPES.APPLICATION_RESPONSE]: MESSAGE_BUSINESS_HOURS.STANDARD,
  [MESSAGE_TYPES.APPLICATION_STATUS_UPDATE]: MESSAGE_BUSINESS_HOURS.STANDARD,
  [MESSAGE_TYPES.NEGOTIATION]: MESSAGE_BUSINESS_HOURS.STANDARD,
  [MESSAGE_TYPES.RATE_NEGOTIATION]: MESSAGE_BUSINESS_HOURS.STANDARD,
  [MESSAGE_TYPES.CONTRACT_DISCUSSION]: MESSAGE_BUSINESS_HOURS.FINANCIAL,
  [MESSAGE_TYPES.PROJECT_UPDATE]: MESSAGE_BUSINESS_HOURS.ALWAYS_AVAILABLE,
  [MESSAGE_TYPES.PROJECT_MILESTONE]: MESSAGE_BUSINESS_HOURS.STANDARD,
  [MESSAGE_TYPES.FINANCIAL_TRANSACTION]: MESSAGE_BUSINESS_HOURS.FINANCIAL,
  [MESSAGE_TYPES.DELIVERY_CONFIRMATION]: MESSAGE_BUSINESS_HOURS.STANDARD,

  // Merge in variation message business hours
  ...VARIATION_MESSAGE_TYPE_HOURS
};

// =========================================================
// MESSAGE TEMPLATE KEYS
// =========================================================

/**
 * Message Template Keys
 * Standard template identifiers for common message scenarios
 */
export const MESSAGE_TEMPLATE_KEYS = {
  // Application Templates
  PREMIUM_APPLICATION: 'premium-application',
  STANDARD_APPLICATION: 'standard-application',
  BEGINNER_APPLICATION: 'beginner-application',

  // Response Templates
  ACCEPTANCE_WITH_NEGOTIATION: 'acceptance-with-negotiation',
  ACCEPTANCE_DIRECT: 'acceptance-direct',
  REJECTION_WITH_FEEDBACK: 'rejection-with-feedback',
  REJECTION_STANDARD: 'rejection-standard',

  // Milestone Templates
  MILESTONE_PAYMENT: 'milestone-payment',
  QUARTER_COMPLETE: 'quarter-complete',
  HALF_COMPLETE: 'half-complete',
  THREE_QUARTER_COMPLETE: 'three-quarter-complete',
  PROJECT_COMPLETE: 'project-complete',

  // Update Templates
  PROGRESS_UPDATE: 'progress-update',
  DELAY_NOTIFICATION: 'delay-notification',
  QUALITY_ISSUE: 'quality-issue',
  SCHEDULE_CHANGE: 'schedule-change',

  // Payment Templates
  SINGLE_PAYMENT: 'single-payment',
  BATCH_PAYMENT: 'batch-payment',

  // Merge in variation message template keys
  ...VARIATION_MESSAGE_TEMPLATE_KEYS
};

// =========================================================
// HELPER FUNCTIONS
// =========================================================

/**
 * Get display name for a message type
 * @param {string} messageType - The message type constant
 * @returns {string} The display name
 */
export const getMessageTypeDisplayName = (messageType) => {
  return MESSAGE_TYPE_CONFIG[messageType]?.displayName || messageType;
};

/**
 * Get icon for a message type
 * @param {string} messageType - The message type constant
 * @returns {string} The icon name
 */
export const getMessageTypeIcon = (messageType) => {
  return MESSAGE_TYPE_CONFIG[messageType]?.icon || 'comment';
};

/**
 * Get color for a message type
 * @param {string} messageType - The message type constant
 * @returns {string} The color name
 */
export const getMessageTypeColor = (messageType) => {
  return MESSAGE_TYPE_CONFIG[messageType]?.color || 'grey';
};

/**
 * Check if a message type requires authentication
 * @param {string} messageType - The message type constant
 * @returns {boolean} True if authentication is required
 */
export const requiresAuthentication = (messageType) => {
  return MESSAGE_TYPE_CONFIG[messageType]?.requiresAuth !== false;
};

/**
 * Check if a message type requires audit logging
 * @param {string} messageType - The message type constant
 * @returns {boolean} True if audit logging is required
 */
export const requiresAudit = (messageType) => {
  return MESSAGE_TYPE_CONFIG[messageType]?.requiresAudit === true;
};

/**
 * Get rate limits for a message type
 * @param {string} messageType - The message type constant
 * @returns {object} Object with hourly and daily limits
 */
export const getMessageRateLimits = (messageType) => {
  return MESSAGE_RATE_LIMITS[messageType] || { hourly: 50, daily: 500 };
};

/**
 * Get business hours for a message type
 * @param {string} messageType - The message type constant
 * @returns {object} Business hours configuration
 */
export const getMessageBusinessHours = (messageType) => {
  return MESSAGE_TYPE_HOURS[messageType] || MESSAGE_BUSINESS_HOURS.ALWAYS_AVAILABLE;
};

/**
 * Get all message types as an array
 * @returns {Array} Array of message type values
 */
export const getAllMessageTypes = () => {
  return Object.values(MESSAGE_TYPES);
};

/**
 * Get all message types for a specific role
 * @param {string} role - The user role
 * @returns {Array} Array of message types available to the role
 */
export const getMessageTypesForRole = (role) => {
  const types = [];
  Object.entries(MESSAGE_TYPE_CONFIG).forEach(([type, config]) => {
    if (config.flow === MESSAGE_FLOW.BIDIRECTIONAL) {
      types.push(type);
    } else if (role === MESSAGE_ROLES.VOICE_ACTOR &&
      config.flow === MESSAGE_FLOW.VOICE_ACTOR_TO_BOOK_CREATOR) {
      types.push(type);
    } else if (role === MESSAGE_ROLES.BOOK_CREATOR &&
      config.flow === MESSAGE_FLOW.BOOK_CREATOR_TO_VOICE_ACTOR) {
      types.push(type);
    } else if (role === MESSAGE_ROLES.ADMIN) {
      types.push(type); // Admins can send all message types
    }
  });
  return types;
};

// Re-export variation helper functions
export { getRetakeReasonConfig, getRetakeReasonOptions };

// =========================================================
// EXPORT DEFAULT
// =========================================================

const messageConstants = {
  MESSAGE_TYPES,
  MESSAGE_PRIORITY,
  MESSAGE_SECURITY_LEVELS,
  MESSAGE_STATUS,
  MESSAGE_ROLES,
  MESSAGE_FLOW,
  MESSAGE_TYPE_CONFIG,
  MESSAGE_RATE_LIMITS,
  MESSAGE_BUSINESS_HOURS,
  MESSAGE_TYPE_HOURS,
  MESSAGE_TEMPLATE_KEYS,
  RETAKE_REASONS,
  RETAKE_REASON_CONFIG,
  // Helper functions
  getMessageTypeDisplayName,
  getMessageTypeIcon,
  getMessageTypeColor,
  requiresAuthentication,
  requiresAudit,
  getMessageRateLimits,
  getMessageBusinessHours,
  getAllMessageTypes,
  getMessageTypesForRole,
  getRetakeReasonConfig,
  getRetakeReasonOptions
};

export default messageConstants;
